import { useEffect } from 'react';
import { Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';

const CONTACT_EMAIL = 'info@startklar.digital';
const CONTACT_PHONE_DISPLAY = '+49 152 07 42 93 38';
const CONTACT_PHONE_TEL = '+4915207429338';

const META_TITLE = 'BAFA Berater werden – Voraussetzungen, Ablauf & Tipps | StartKlar.Digital';
const META_DESCRIPTION =
  'Wie wird man BAFA-Berater? Voraussetzungen, Ablauf der Zulassung und typische Fehler – praxisnah erklärt von erfahrenen BAFA-Beratern. Jetzt Erstgespräch vereinbaren.';

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Wie wird man BAFA-Berater?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Wer als BAFA-Berater gelistet werden möchte, registriert sich im BAFA-Beraterportal, reicht die geforderten Nachweise zu Selbstständigkeit, Beratungstätigkeit, fachlicher Eignung und Qualitätssicherung ein und erhält nach positiver Prüfung eine Berater-ID sowie den Status „gelistet".',
      },
    },
    {
      '@type': 'Question',
      name: 'Wie lange dauert die BAFA-Zulassung?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Die Dauer hängt von der Vollständigkeit der eingereichten Unterlagen ab. Sind alle Nachweise sauber aufbereitet, ist eine Bearbeitung innerhalb weniger Wochen üblich. Fehlende oder unklare Nachweise verlängern den Prozess deutlich.',
      },
    },
    {
      '@type': 'Question',
      name: 'Ist man als BAFA-Berater zertifiziert?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Nein. Das BAFA vergibt keine Zertifizierung. Gelistete Berater sind im Rahmen des Förderprogramms „Förderung von Unternehmensberatungen für KMU" zugelassen. Der korrekte Begriff ist „gelisteter BAFA-Berater", nicht „zertifiziert".',
      },
    },
    {
      '@type': 'Question',
      name: 'Wie viel Umsatz muss man als Berater nachweisen?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Die BAFA-Richtlinie verlangt, dass die entgeltliche Unternehmensberatung den überwiegenden Teil der Geschäftstätigkeit ausmacht. Konkrete Nachweisformen und Schwellen ergeben sich aus der jeweils gültigen Fassung der Richtlinie und den Merkblättern des BAFA.',
      },
    },
    {
      '@type': 'Question',
      name: 'Kann ich als Freelancer BAFA-Berater werden?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Ja. Selbstständige Einzelberater können sich listen lassen, sofern sie die Voraussetzungen zur Beratungstätigkeit, fachlichen Eignung und Qualitätssicherung erfüllen und die entsprechenden Nachweise vorlegen.',
      },
    },
    {
      '@type': 'Question',
      name: 'Welche Nachweise verlangt das BAFA konkret?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Üblicherweise werden Nachweise zur Selbstständigkeit, zur überwiegenden Beratungstätigkeit, zur fachlichen Eignung sowie zur Qualitätssicherung verlangt. Die genaue Liste ergibt sich aus dem aktuellen Merkblatt des BAFA zum Beraterportal.',
      },
    },
  ],
};

export default function BafaBeraterWerden() {
  const { darkMode } = useDarkMode();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = META_TITLE;

    let descTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const hadDescTag = !!descTag;
    const previousDesc = descTag?.content ?? '';
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = META_DESCRIPTION;

    return () => {
      document.title = previousTitle;
      if (hadDescTag && descTag) {
        descTag.content = previousDesc;
      } else if (descTag) {
        descTag.remove();
      }
    };
  }, []);

  const ctaButtonPrimary = `inline-block px-5 py-3 rounded-lg font-semibold transition-colors ${
    darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
  }`;
  const ctaButtonSecondary = `inline-block px-5 py-3 rounded-lg font-semibold border transition-colors ${
    darkMode
      ? 'border-slate-600 text-slate-100 hover:bg-slate-800'
      : 'border-slate-300 text-slate-800 hover:bg-slate-100'
  }`;
  const inlineLink = darkMode
    ? 'text-blue-400 hover:text-blue-300 underline'
    : 'text-blue-600 hover:text-blue-700 underline';

  return (
    <div
      className={`min-h-screen ${
        darkMode ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-700'
      }`}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <article
          className={`[&_h1]:text-3xl md:[&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:mb-6 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-4 [&_p]:leading-relaxed [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-4 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:my-1.5 [&_li]:leading-relaxed ${
            darkMode
              ? '[&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_strong]:text-white'
              : '[&_h1]:text-slate-900 [&_h2]:text-slate-900 [&_h3]:text-slate-900 [&_strong]:text-slate-900'
          }`}
        >
          <h1>Wie wird man BAFA-Berater? Voraussetzungen, Ablauf und Tipps aus der Praxis</h1>

          <p>
            <strong>BAFA-Berater werden</strong> ist für viele Selbstständige, Agenturen und Unternehmensberater ein lohnender Schritt: Gelistete Berater erscheinen in der offiziellen BAFA-Beratersuche, ihre Kunden erhalten Zuschüsse auf das Beratungshonorar, und der Status schafft Vertrauen am Markt. Dieser Artikel beschreibt, was <em>„BAFA-Berater"</em> genau bedeutet, welche Voraussetzungen das BAFA prüft, wie der Ablauf der Zulassung funktioniert und welche Stolperfallen du vermeiden solltest.
          </p>

          <h2>Was bedeutet „BAFA-Berater" überhaupt?</h2>
          <p>
            Das Bundesamt für Wirtschaft und Ausfuhrkontrolle (BAFA) verwaltet das Förderprogramm <strong>„Förderung von Unternehmensberatungen für KMU"</strong>. Kleine und mittlere Unternehmen erhalten darüber einen Zuschuss zu den Nettokosten einer qualifizierten Unternehmensberatung. Voraussetzung: Die Beratung wird von einer beim BAFA <strong>gelisteten Beratungsgesellschaft</strong> oder einem gelisteten Einzelberater erbracht.
          </p>
          <p>
            Wichtig ist die Unterscheidung zwischen <em>Beratung</em> und <em>Umsetzung</em>. Das BAFA fördert ausschließlich die Beratungsleistung – also Analyse, Strategie, Konzept und Handlungsempfehlung. Reine Umsetzungsarbeit wie Webentwicklung, Designproduktion oder der Aufbau technischer Systeme ist nicht förderfähig. Wer BAFA-Berater werden möchte, muss also nachweisen, dass der Schwerpunkt seiner Tätigkeit echte Beratung ist.
          </p>
          <p>
            Der Status als gelisteter Berater ist kein Zertifikat und keine Gütesiegelverleihung. Es ist eine Zulassung zur Teilnahme am Förderprogramm. Der gängige Begriff ist <strong>„gelisteter BAFA-Berater"</strong> – nicht „BAFA-zertifiziert".
          </p>

          <h2>BAFA Berater Voraussetzungen – einfach erklärt</h2>
          <p>
            Um in das BAFA-Beraterportal aufgenommen zu werden, müssen mehrere Voraussetzungen gleichzeitig erfüllt sein. Sie ergeben sich aus der jeweils gültigen Fassung der BAFA-Richtlinie und den dazugehörigen Merkblättern.
          </p>

          <h3>Selbstständige oder unternehmerische Tätigkeit</h3>
          <p>
            Der Berater muss als selbstständiger Unternehmer, Freiberufler oder in Form einer Gesellschaft tätig sein. Angestellte Berater ohne eigene unternehmerische Tätigkeit können sich nicht als eigenständige BAFA-Berater listen lassen.
          </p>

          <h3>Überwiegende Beratungstätigkeit</h3>
          <p>
            Die entgeltliche Unternehmensberatung muss den <strong>überwiegenden Teil</strong> der Geschäftstätigkeit ausmachen. Agenturen, deren Hauptumsatz aus Webentwicklung, Design oder Produktumsetzung stammt, erfüllen diese Voraussetzung typischerweise nicht, auch wenn sie Beratung als Nebenleistung anbieten.
          </p>

          <h3>Fachliche Eignung</h3>
          <p>
            Der Berater muss nachweisen, dass er fachlich in der Lage ist, Unternehmen qualifiziert zu beraten. Dies kann über Ausbildung, Qualifikationen, Referenzen und einschlägige Berufserfahrung belegt werden.
          </p>

          <h3>Qualitätssicherung</h3>
          <p>
            Das BAFA verlangt ein System zur Qualitätssicherung der Beratungsleistung. In der Praxis sind ein dokumentiertes QM-System, Beratungsprozessbeschreibungen und Feedback-Instrumente üblich. Die konkreten Anforderungen ergeben sich aus dem aktuellen Merkblatt des BAFA.
          </p>

          <h2>BAFA Berater werden – der Ablauf Schritt für Schritt</h2>
          <p>
            Der Weg zum gelisteten Berater folgt einem klaren Prozess. Wer strukturiert vorgeht und die Unterlagen vollständig einreicht, kommt schneller zum Ziel.
          </p>
          <ol>
            <li>
              <strong>Registrierung im BAFA-Beraterportal.</strong> Im Portal wird zunächst ein Account angelegt. Hier werden Unternehmensdaten, Kontaktdaten und Tätigkeitsschwerpunkte hinterlegt.
            </li>
            <li>
              <strong>Vergabe der Berater-ID.</strong> Nach erfolgreicher Anmeldung erhält der Berater eine eindeutige Berater-ID. Sie identifiziert ihn in allen weiteren Verfahrensschritten und in Förderanträgen seiner Kunden.
            </li>
            <li>
              <strong>Upload der Nachweise.</strong> Alle geforderten Dokumente werden digital hochgeladen: Nachweise zur Selbstständigkeit, zur Beratungstätigkeit, zur fachlichen Eignung und zur Qualitätssicherung.
            </li>
            <li>
              <strong>Prüfung durch das BAFA.</strong> Das BAFA prüft die Unterlagen auf Vollständigkeit und Plausibilität. Bei Rückfragen oder fehlenden Dokumenten wird der Berater schriftlich kontaktiert.
            </li>
            <li>
              <strong>Status „gelistet".</strong> Nach positiver Prüfung erscheint der Berater in der öffentlichen Beratersuche. Seine Kunden können Förderanträge unter Angabe der Berater-ID einreichen.
            </li>
          </ol>

          <h2>Welche Nachweise verlangt das BAFA?</h2>
          <p>
            Die konkret geforderten Unterlagen ergeben sich aus dem aktuellen Merkblatt des BAFA zum Beraterportal und können sich ändern. Typischerweise werden folgende Nachweise verlangt:
          </p>
          <ul>
            <li>Gewerbeanmeldung oder Bestätigung der freiberuflichen Tätigkeit</li>
            <li>Nachweise zur überwiegenden Beratungstätigkeit, etwa in Form von Referenzprojekten oder einer Tätigkeitsaufstellung</li>
            <li>Lebenslauf mit Ausbildung, Qualifikationen und relevanter Berufserfahrung</li>
            <li>Dokumentation des Qualitätssicherungssystems</li>
            <li>Beschreibung der Beratungsschwerpunkte und Zielgruppen</li>
          </ul>
          <p>
            Die Qualität der Nachweise entscheidet häufig über die Dauer des Verfahrens. Saubere Dokumente, klare Referenzen und eine nachvollziehbare Tätigkeitsbeschreibung verkürzen die Prüfung deutlich.
          </p>

          <h2>Typische Fehler bei der BAFA-Zulassung</h2>
          <p>
            In der Praxis scheitern viele Anträge nicht an der fachlichen Eignung, sondern an vermeidbaren formalen Fehlern.
          </p>
          <ul>
            <li>
              <strong>Unklare Abgrenzung Beratung vs. Umsetzung.</strong> Wer seine Beratungstätigkeit nicht klar von Umsetzungs- oder Dienstleistungsarbeit abgrenzt, riskiert die Ablehnung wegen fehlender überwiegender Beratungstätigkeit.
            </li>
            <li>
              <strong>Unvollständige Referenzen.</strong> Referenzprojekte ohne Kundenname, Zeitraum, Beratungsschwerpunkt und Umfang sind für das BAFA nicht belastbar.
            </li>
            <li>
              <strong>Lücken im QM-System.</strong> Ein allgemeiner Verweis auf „Qualitätssicherung" genügt nicht. Erwartet werden konkrete Prozesse, Vorlagen und Rückkopplungsschleifen.
            </li>
            <li>
              <strong>Falscher Begriff.</strong> Wer sich öffentlich als „BAFA-zertifiziert" bezeichnet, verwendet einen unzutreffenden Begriff und riskiert wettbewerbsrechtliche Abmahnungen. Korrekt ist „gelisteter BAFA-Berater".
            </li>
            <li>
              <strong>Fehlende Aktualisierung.</strong> Änderungen in der Gesellschafterstruktur, am Qualifikationsprofil oder am Tätigkeitsschwerpunkt müssen im Portal gepflegt werden. Veraltete Einträge können den Status gefährden.
            </li>
          </ul>

          <h2>Wann lohnt es sich, BAFA-Berater zu werden?</h2>
          <p>
            Die BAFA-Listung ist kein Selbstzweck. Sie lohnt sich vor allem für Berater, die tatsächlich KMU beraten, Analyse- und Strategieleistungen als Kern ihrer Arbeit verstehen und mittel- bis langfristig im Markt positioniert sein wollen. Für reine Umsetzungs-Agenturen ist der Status weder passend noch erreichbar.
          </p>
          <p>
            Ein klarer Vorteil ist die Sichtbarkeit in der offiziellen Beratersuche. Viele KMU suchen gezielt gelistete Berater, weil sie den Zuschuss nutzen möchten. Wer beraten kann und sich sauber listen lässt, erschließt sich eine Zielgruppe, die aktiv nach Beratung sucht – ein Unterschied zu rein kalter Akquise.
          </p>
          <p>
            Nicht lohnenswert ist die Listung, wenn die Beratungstätigkeit nur einen Nebenanteil ausmacht, wenn das QM-System lediglich „auf dem Papier" existiert, oder wenn der Antragsprozess ohne echte Expertise durchlaufen wird. Eine Ablehnung kostet Zeit und schwächt die spätere Positionierung.
          </p>

          <h2>Unterstützung bei der BAFA-Zulassung</h2>
          <p>
            Wir begleiten Berater, Agenturen und Selbstständige seit Jahren durch den BAFA-Zulassungsprozess und die spätere Beratungspraxis. Unsere Unterstützung ist praxisnah und konkret:
          </p>
          <ul>
            <li>
              <strong>Zulassung.</strong> Wir prüfen, ob du die Voraussetzungen erfüllst, und strukturieren deine Nachweise so, dass sie beim BAFA Bestand haben. Kein Lehrbuch-Wissen, sondern erprobte Vorlagen und Checklisten.
            </li>
            <li>
              <strong>Positionierung.</strong> Gelistet zu sein reicht nicht – du musst für KMU auffindbar und wählbar werden. Wir helfen bei Beratungsschwerpunkt, Zielgruppendefinition und Portalprofil.
            </li>
            <li>
              <strong>Berichte und Prozesse.</strong> Förderfähige Beratung endet mit einem BAFA-konformen Beratungsbericht. Wir stellen Vorlagen bereit, automatisieren wiederkehrende Teile und sorgen dafür, dass Folgeförderungen deiner Kunden nicht an Formfehlern scheitern.
            </li>
          </ul>
          <p>
            Als gelistete BAFA-Berater kennen wir den Prozess aus beiden Perspektiven: aus der eigenen Zulassung und aus der täglichen Arbeit mit KMU. Wenn du unsicher bist, ob sich die Listung für dich lohnt oder wie du sie angehen sollst, ist ein kostenfreies Erstgespräch der schnellste Weg zu Klarheit.
          </p>
          <p className="!mt-6 flex flex-wrap gap-3">
            <Link
              to={`/kontakt?subject=${encodeURIComponent('BAFA-Zulassung / Beratung')}&message=${encodeURIComponent('Ich interessiere mich für Unterstützung bei der BAFA-Zulassung und möchte ein kostenfreies Erstgespräch vereinbaren.')}`}
              className={ctaButtonPrimary}
            >
              Erstgespräch anfragen
            </Link>
            <a href={`tel:${CONTACT_PHONE_TEL}`} className={ctaButtonSecondary}>
              {CONTACT_PHONE_DISPLAY}
            </a>
          </p>

          <h2>Häufige Fragen zur BAFA-Beratung</h2>

          <h3>Wie wird man BAFA-Berater?</h3>
          <p>
            Über das BAFA-Beraterportal: Registrierung, Einreichung der Nachweise zu Selbstständigkeit, überwiegender Beratungstätigkeit, fachlicher Eignung und Qualitätssicherung, anschließende Prüfung durch das BAFA und Listung mit Berater-ID.
          </p>

          <h3>Wie lange dauert die Zulassung?</h3>
          <p>
            Bei vollständigen und sauber aufbereiteten Unterlagen typischerweise wenige Wochen. Rückfragen, fehlende Nachweise oder unklare Referenzen verlängern das Verfahren erheblich.
          </p>

          <h3>Ist man BAFA-zertifiziert?</h3>
          <p>
            Nein. Das BAFA vergibt keine Zertifizierung. Der korrekte Begriff lautet <strong>„gelisteter BAFA-Berater"</strong>. Die Bezeichnung „BAFA-zertifiziert" ist unzutreffend und kann wettbewerbsrechtlich abgemahnt werden.
          </p>

          <h3>Wie viel Umsatz muss man nachweisen?</h3>
          <p>
            Die Richtlinie verlangt, dass die entgeltliche Unternehmensberatung den überwiegenden Teil der Geschäftstätigkeit ausmacht. Welche Belege und Schwellen im Detail gelten, ergibt sich aus der aktuell gültigen Fassung der Richtlinie und dem Merkblatt des BAFA.
          </p>

          <h3>Kann ich als Freelancer BAFA-Berater werden?</h3>
          <p>
            Ja. Selbstständige Einzelberater sind ausdrücklich zugelassen, sofern Beratungstätigkeit, fachliche Eignung und Qualitätssicherung nachgewiesen werden.
          </p>

          <h3>Welche Nachweise verlangt das BAFA konkret?</h3>
          <p>
            Übliche Anforderungen sind Gewerbeanmeldung oder Freiberuflernachweis, Tätigkeitsaufstellung bzw. Referenzen, Lebenslauf mit Qualifikationen sowie eine Dokumentation des Qualitätssicherungssystems. Die verbindliche Liste steht im aktuellen Merkblatt des BAFA.
          </p>

          <h2>Fazit</h2>
          <p>
            BAFA-Berater zu werden, lohnt sich für alle, die <strong>echte Beratungskompetenz</strong> mitbringen und Unternehmen strategisch voranbringen wollen. Die Listung ist kein Marketinglabel, sondern ein Zulassungsverfahren mit klaren inhaltlichen Anforderungen. Wer sauber vorbereitet, die Abgrenzung zwischen Beratung und Umsetzung sichtbar macht und ein belastbares Qualitätssystem mitbringt, kommt in der Regel zügig zum Status.
          </p>
          <p>
            Wer schnell Orientierung braucht, ob sich die Zulassung lohnt und welche Nachweise konkret passen, vereinbart am besten ein kurzes Erstgespräch. Du erreichst uns per E-Mail unter{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className={inlineLink}>
              {CONTACT_EMAIL}
            </a>{' '}
            oder telefonisch unter{' '}
            <a href={`tel:${CONTACT_PHONE_TEL}`} className={inlineLink}>
              {CONTACT_PHONE_DISPLAY}
            </a>
            .
          </p>
        </article>
      </div>
    </div>
  );
}
